// Register screen — step 3 of the onboarding funnel (guests land here from
// /onboarding). Email-only: the account is created without a password and the
// 6-digit email code both verifies the mailbox and logs the user in
// (boards card «вход по ключу, а не по паролю»).

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useYandexAuth } from '../../hooks/useYandexAuth'
import { authApi } from '../../api/auth'
import { messageFromError } from '../../api/client'
import { getInviteToken, clearInviteToken } from '../../lib/invite'
import { useCountdown } from '../../hooks/useCountdown'
import CodeInput from '../../components/CodeInput'
import YandexButton from '../../components/YandexButton'
import { useOnboardingStore } from '../../store/onboardingStore'
import { OnboardingBrand } from '../../features/onboarding'
import shell from '../Onboarding/Onboarding.module.css'
import s from '../auth.module.css'

// `initialDisplayName` is the name from onboarding step 1. The field stays in
// the form so it is submitted, but it is not shown. Invite validation still
// fills it only when it is empty.
export function RegisterForm({ initialDisplayName = '' }: { initialDisplayName?: string }) {
  const { register, verifyCode } = useAuth()
  const yandex = useYandexAuth()
  // Validate the invite token before trusting it: only show the "С возвращением!"
  // banner for a valid token, and prefill the server display name (original
  // _detectInviteToken → /invite/{token}/validate, auth.js:809-847).
  const [invited, setInvited] = useState(false)

  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState(initialDisplayName)
  const [consentAge, setConsentAge] = useState(false)
  const [consentTerms, setConsentTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Verification step state
  const [verifyEmail, setVerifyEmail] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [verifying, setVerifying] = useState(false)
  const resend = useCountdown()

  useEffect(() => {
    if (verifyEmail) resend.start(60)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyEmail])

  // Validate the invite token once on mount (banner + display-name prefill).
  useEffect(() => {
    const token = getInviteToken()
    if (!token) return
    authApi
      .validateInvite(token)
      .then((data) => {
        if (data?.valid) {
          setInvited(true)
          if (data.display_name) setDisplayName((cur) => cur || data.display_name || '')
        } else {
          clearInviteToken() // invalid/expired → don't show the banner
        }
      })
      .catch(() => {
        /* network error — keep the token, just skip the banner */
      })
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email) return setError('Email обязателен')
    if (!consentAge || !consentTerms) return setError('Необходимо подтвердить оба согласия')

    setLoading(true)
    try {
      // Passwordless registration — the backend accepts a null password; the
      // emailed code is the login key from here on.
      const result = await register({ email, displayName })
      if (result.needsVerification) setVerifyEmail(result.email ?? null)
      // else: useAuth already navigated to chat
    } catch (err) {
      setError(messageFromError(err, 'Ошибка регистрации'))
    } finally {
      setLoading(false)
    }
  }

  const submitCode = async (value?: string) => {
    const finalCode = value || code
    if (finalCode.length !== 6 || !verifyEmail) return
    setVerifying(true)
    setCodeError('')
    try {
      await verifyCode({ email: verifyEmail, code: finalCode })
    } catch (err) {
      setCodeError(messageFromError(err, 'Неверный код'))
      setCode('')
    } finally {
      setVerifying(false)
    }
  }

  const resendCode = async () => {
    if (!verifyEmail) return
    try {
      await authApi.resendVerification({ email: verifyEmail })
    } catch {
      /* ignore — UI just restarts the cooldown */
    }
    resend.start(60)
  }

  if (verifyEmail) {
    return (
      <>
        <div className={s.header}>
          <h1 className={s.title}>Подтвердите email</h1>
          <p className={s.subtitle}>
            Код отправлен на <strong>{verifyEmail}</strong>
          </p>
        </div>

        <CodeInput value={code} onChange={setCode} onComplete={submitCode} disabled={verifying} />

        <button
          className={s.button}
          style={{ marginTop: 16 }}
          onClick={() => submitCode()}
          disabled={verifying || code.length !== 6}
        >
          {verifying ? 'Проверка...' : 'Подтвердить'}
        </button>

        {codeError && <p className={s.error} style={{ marginTop: 12 }}>{codeError}</p>}

        <p className={s.muted}>
          {resend.active ? (
            `Повторная отправка через ${resend.remaining} сек.`
          ) : (
            <button type="button" className={s.link} onClick={resendCode}>
              Отправить код повторно
            </button>
          )}
        </p>
      </>
    )
  }

  return (
    <>
        <div className={`${s.header} ${s.stepHeader}`}>
          {invited ? (
            <div className={s.inviteBanner}>
              <div className={s.inviteBannerIcon}>👋</div>
              <h2 className={s.inviteBannerTitle}>С возвращением!</h2>
              <p className={s.inviteBannerText}>
                Мы узнали вас. Создайте аккаунт для веб-приложения — ваша история и подписка из
                Telegram сохранятся.
              </p>
            </div>
          ) : (
            <>
              <h1 className={`${s.title} ${s.stepTitle}`}>Куда сохранить<br />ваш разговор?</h1>
              <p className={s.subtitle}>
                Введите email, чтобы мы сохранили ваш разговор и предложили персональные рекомендации.
              </p>
            </>
          )}
        </div>

        <form className={s.form} onSubmit={submit}>
          <div className={`${s.fieldGroup} ${s.stepFields}`}>
            <input
              className={s.input}
              type="email"
              placeholder="name@example.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input type="hidden" value={displayName} readOnly />
          </div>

          {error && <p className={s.error}>{error}</p>}

          <button className={s.button} type="submit" disabled={loading}>
            {loading ? 'Создание...' : 'Начать разговор'}
          </button>
        </form>

        <div className={s.socialGap}>
          <YandexButton
            label="Зарегистрироваться через Яндекс"
            onClick={() => {
              // 152-ФЗ: creating an account requires explicit consent (parent d8f3a7b1).
              if (!consentAge || !consentTerms) {
                setError('Подтвердите оба согласия')
                return
              }
              yandex.signIn()
            }}
            disabled={loading || yandex.loading}
          />
        </div>

        <div className={`${s.consentGroup} ${s.consentBelow}`}>
          <label className={s.consentCheck}>
            <input type="checkbox" checked={consentTerms} onChange={(e) => setConsentTerms(e.target.checked)} />
            <span>
              Я&nbsp;соглашаюсь на&nbsp;обработку моих персональных данных и&nbsp;принимаю условия{' '}
              <a href="https://humanteq.com/consent_user_agreement" target="_blank" rel="noopener noreferrer">
                Пользовательского соглашения
              </a>
              .
            </span>
          </label>
          <label className={s.consentCheck}>
            <input type="checkbox" checked={consentAge} onChange={(e) => setConsentAge(e.target.checked)} />
            <span>
              Я&nbsp;подтверждаю, что мне есть 18&nbsp;лет, я&nbsp;не&nbsp;состою на&nbsp;психиатрическом наблюдении и&nbsp;у&nbsp;меня нет намерений причинить вред себе и&nbsp;окружающим
            </span>
          </label>
        </div>

        <div className={s.authBottom}>
          Уже есть аккаунт? <Link to="/login">Войти</Link>
        </div>
    </>
  )
}

export default function Register() {
  const pendingName = useOnboardingStore((state) => state.pendingName) ?? ''
  return (
    <div className={`${shell.screen} ${shell.registration}`}>
      <div className={shell.content}>
        <OnboardingBrand step={2} />
        <div className={shell.bodyArea}>
          <RegisterForm initialDisplayName={pendingName} />
        </div>
      </div>
    </div>
  )
}
